import { type FormEvent, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { forumBodyError, forumThreadDetailView } from '@lms/shared-types';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { CourseHubShell } from '@/components/courses/CourseHubShell';
import { FORUM_STAFF_PAGE_DESCRIPTION } from '@/features/course-forum/constants';
import { CourseForumThreadFlow } from '@/features/course-forum/CourseForumThreadFlow';
import { type ForumPost, type ForumThreadDetail } from '@/features/course-forum/types';
import { api } from '@/lib/api';
import { courseTabHref } from '@/lib/course-admin-navigation';
import { forumThreadDeleteConfirm, forumThreadDeleteSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface CourseMeta {
  name: string;
}

function draftKey(threadId: string) {
  return `lms-forum-draft:${threadId}`;
}

export function CourseForumModerationThreadPage() {
  const { id, threadId } = useParams<{ id: string; threadId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reply, setReply] = useState(() => {
    if (!threadId) return '';
    try {
      return sessionStorage.getItem(draftKey(threadId)) ?? '';
    } catch {
      return '';
    }
  });
  const [replyError, setReplyError] = useState<string | null>(null);
  const [draftSaved, setDraftSaved] = useState(false);

  const { data: course } = useQuery({
    queryKey: ['courses', id],
    queryFn: () => api<CourseMeta>(`/courses/${id}`),
    enabled: Boolean(id),
  });

  const { data: thread, isLoading, error } = useQuery({
    queryKey: ['courses', id, 'forum', 'threads', threadId],
    queryFn: () => api<ForumThreadDetail>(`/courses/${id}/forum/threads/${threadId}`),
    enabled: Boolean(id && threadId),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['courses', id, 'forum'] });
    await queryClient.invalidateQueries({ queryKey: ['courses', id, 'forum', 'threads', threadId] });
  };

  const replyMutation = useMutation({
    mutationFn: (body: string) =>
      api<ForumThreadDetail>(`/courses/${id}/forum/threads/${threadId}/reply`, {
        method: 'POST',
        body: { body },
      }),
    onSuccess: async () => {
      setReply('');
      setReplyError(null);
      setDraftSaved(false);
      if (threadId) {
        try {
          sessionStorage.removeItem(draftKey(threadId));
        } catch {
          /* ignore */
        }
      }
      await invalidate();
    },
  });

  const moderateMutation = useMutation({
    mutationFn: (payload: { isPinned?: boolean; isLocked?: boolean }) =>
      api<ForumThreadDetail>(`/courses/${id}/forum/threads/${threadId}`, {
        method: 'PATCH',
        body: payload,
      }),
    onSuccess: invalidate,
  });

  const deleteThreadMutation = useMutation({
    mutationFn: () =>
      api(`/courses/${id}/forum/threads/${threadId}`, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      setConfirmDelete(false);
      await queryClient.invalidateQueries({ queryKey: ['courses', id, 'forum'] });
      toast(forumThreadDeleteSuccessToast());
      navigate(id ? courseTabHref(id, 'foro', searchParams) : '/courses');
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el hilo'));
    },
  });

  const deletePostMutation = useMutation({
    mutationFn: (postId: string) =>
      api<ForumThreadDetail>(`/courses/${id}/forum/threads/${threadId}/posts/${postId}`, {
        method: 'DELETE',
      }),
    onSuccess: invalidate,
  });

  function handleReply(event: FormEvent) {
    event.preventDefault();
    const nextError = forumBodyError(reply);
    if (nextError) {
      setReplyError(nextError);
      return;
    }
    setReplyError(null);
    replyMutation.mutate(reply.trim());
  }

  function quotePost(post: ForumPost) {
    const quoted = post.body
      .trim()
      .split('\n')
      .slice(0, 4)
      .map((line) => `> ${line}`)
      .join('\n');
    setReply((current) => `${current.trim() ? `${current.trim()}\n\n` : ''}${quoted}\n\n`);
    setReplyError(null);
    setDraftSaved(false);
  }

  function saveDraft() {
    if (!threadId) return;
    try {
      sessionStorage.setItem(draftKey(threadId), reply);
      setDraftSaved(true);
    } catch {
      setDraftSaved(false);
    }
  }

  if (!id || !threadId) return null;

  const view = forumThreadDetailView(isLoading, thread);
  const deleteCopy = forumThreadDeleteConfirm(view.status === 'ready' ? view.thread.title : '');
  const openingPost = view.status === 'ready' ? view.openingPost : undefined;
  const replies = view.status === 'ready' ? view.replies : [];

  return (
    <CourseHubShell
      courseId={id}
      courseName={course?.name ?? 'Curso'}
      activeTab="foro"
      description={FORUM_STAFF_PAGE_DESCRIPTION}
    >
      <div className="lms-course-forum-page lms-course-forum-thread-page lms-course-forum-mod-thread-page">
        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {(error as Error).message}
          </p>
        ) : null}

        {view.status === 'ready' ? (
          <CourseForumThreadFlow
            thread={view.thread}
            openingPost={openingPost}
            replies={replies}
            backTo={courseTabHref(id, 'foro', searchParams)}
            staffView
            reply={reply}
            replyError={replyError ?? (replyMutation.error instanceof Error ? replyMutation.error.message : null)}
            replyPending={replyMutation.isPending}
            onReplyChange={(value) => {
              setReply(value);
              setReplyError(null);
              setDraftSaved(false);
            }}
            onReplySubmit={handleReply}
            onQuote={quotePost}
            replyTitle="Respuesta como docente"
            replyPlaceholder="Escribe aquí tu retroalimentación pedagógica, aclaración u orden institucional..."
            onSaveDraft={saveDraft}
            draftSaved={draftSaved}
            actionsPending={moderateMutation.isPending || deleteThreadMutation.isPending || deletePostMutation.isPending}
            onTogglePin={() => moderateMutation.mutate({ isPinned: !view.thread.isPinned })}
            onToggleLock={() => moderateMutation.mutate({ isLocked: !view.thread.isLocked })}
            onDeleteThread={() => setConfirmDelete(true)}
            onDeletePost={(post) => {
              if (window.confirm('¿Eliminar este mensaje?')) deletePostMutation.mutate(post.id);
            }}
          />
        ) : error ? null : (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        )}

        <ConfirmModal
          open={confirmDelete}
          title={deleteCopy.title}
          description={deleteCopy.description}
          confirmLabel={deleteCopy.confirmLabel}
          variant={deleteCopy.variant}
          loading={deleteThreadMutation.isPending}
          onConfirm={() => deleteThreadMutation.mutate()}
          onClose={() => {
            if (!deleteThreadMutation.isPending) setConfirmDelete(false);
          }}
        />
      </div>
    </CourseHubShell>
  );
}
